import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "110";
const height = "40";
const viewBox = "0 0 110 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            d="M68.828 25.943c2.046 0 2.681-1.448 3.11-3.19h-.961c-1.33 0-3.54.232-3.54 2.057 0 .755.695 1.133 1.391 1.133zm2.681.147h-.041C70.527 27.307 69.585 28 68.07 28c-1.902 0-3.252-1.092-3.252-3.085 0-3.567 3.887-3.84 6.465-3.84h.961c.082-.294.082-.609.082-.924 0-1.09-1.146-1.405-2.107-1.405-1.208 0-2.374.315-3.458.839l.388-2.245c1.166-.42 2.353-.65 3.581-.65 2.108 0 4.092.797 4.092 3.335 0 1.448-1.043 5.688-1.289 7.723h-2.312l.287-1.658h.001zM.429 16.931h1.986l.47-2.224 2.946-.986-.675 3.21h2.456l-.43 2.057H4.726l-1.002 4.721a4.57 4.57 0 00-.123 1.05c0 .65.43 1.048 1.044 1.048.49 0 .982-.104 1.33-.251l-.47 2.182c-.472.105-1.003.252-1.474.252-1.862 0-3.172-.755-3.172-2.833 0-.839.164-1.658.348-2.517l.778-3.652H0l.43-2.057H.429zm9.311-4.658h2.742l-.573 2.686H9.167l.573-2.686zM8.737 16.93h2.742L9.23 27.738H6.487l2.25-10.807v-.001zm11.023 2.477c-.533-.357-1.249-.546-2.006-.546-2.19 0-3.417 2.098-3.417 4.134 0 1.405.45 2.811 2.333 2.811.675 0 1.514-.189 2.23-.545l-.307 2.225c-.839.419-1.739.503-2.64.503-2.72 0-4.48-1.867-4.48-4.658 0-3.778 2.434-6.652 6.22-6.652 1.023 0 2.005.189 2.742.42l-.675 2.308zM22.767 12h2.741l-1.82 8.856h.04l3.827-3.925h3.56l-5.054 4.932 3.171 5.875h-3.09l-2.72-5.728h-.042l-1.186 5.728h-2.68L22.766 12h.001zm18.232 4.931h1.984l.47-2.224 2.948-.986-.676 3.21h2.456l-.43 2.057h-2.456l-1.002 4.721a4.589 4.589 0 00-.123 1.05c0 .65.43 1.048 1.044 1.048a3.59 3.59 0 001.33-.251l-.471 2.182c-.47.105-1.003.252-1.473.252-1.862 0-3.172-.755-3.172-2.833 0-.839.164-1.658.348-2.517l.778-3.652h-1.985l.43-2.057zm-.917 3.862c0 .818-.103 1.615-.266 2.413h-6.458a6.75 6.75 0 01.291-1.93h3.752c.04-.253.062-.483.062-.735 0-1.155-.594-1.805-1.74-1.805-1.615 0-2.36.95-2.887 2.326a5.952 5.952 0 00-.446 2.144h-.002v.091c0 .104.009.2.013.298h.003c.131 1.7 1.156 2.339 2.91 2.339 1.125 0 2.21-.294 3.233-.777l-.348 2.225c-1.064.336-2.23.608-3.355.608-3.029 0-5.075-1.51-5.075-4.721 0-3.357 2.537-6.589 5.995-6.589 2.599 0 4.318 1.363 4.318 4.113zm9.282-2.666c.082-.419.163-.86.225-1.196h2.558L51.9 18.38h.04c.737-1.112 1.945-1.699 3.233-1.699 1.249 0 2.538.755 2.681 2.056h.041c.696-1.406 2.23-2.056 3.703-2.056 1.576 0 2.926 1.322 2.926 2.959 0 .965-.245 2.056-.47 2.937l-1.105 5.162h-2.742l1.105-5.267c.143-.65.348-1.658.348-2.308 0-.797-.593-1.301-1.29-1.301-1.902 0-2.373 2.266-2.72 3.735l-1.085 5.141h-2.742l1.105-5.267c.143-.65.348-1.658.348-2.308 0-.797-.593-1.301-1.29-1.301-1.861 0-2.373 2.287-2.72 3.735l-1.085 5.141H47.44l1.924-9.61v-.002zm33.604 1.005c-.294-.165-1.052-.396-1.83-.396-.817 0-1.84.231-1.84 1.09 0 1.365 3.56 1.87 3.56 4.513 0 2.812-2.394 3.651-4.747 3.651-1.105 0-2.21-.167-3.213-.65l.594-2.246c.777.44 1.555.84 2.619.84.92 0 1.882-.316 1.882-1.196 0-1.742-3.56-1.91-3.56-4.617 0-2.539 2.394-3.44 4.563-3.44 1.003 0 2.025.188 2.566.331l-.594 2.12zm1.751-2.2h1.985l.47-2.225 2.947-.986-.675 3.21h2.455l-.43 2.057h-2.455l-1.002 4.721c-.062.252-.123.63-.123 1.05 0 .65.43 1.048 1.044 1.048.49 0 .982-.104 1.33-.251l-.47 2.182c-.472.105-1.003.252-1.474.252-1.862 0-3.172-.755-3.172-2.833 0-.839.164-1.658.348-2.517l.778-3.652H84.29l.43-2.057-.001.001zm18.59 1.447c.082-.461.164-.944.245-1.448h2.62l-.369 2.036h.042c.49-1.175 1.84-2.287 3.11-2.287.347 0 .716.02 1.043.105l-.573 2.749a3.396 3.396 0 00-1.043-.168c-1.944 0-2.886 1.783-3.233 3.525l-.982 4.847h-2.742l1.882-9.359zm-1.903 2.414c0 .818-.102 1.615-.266 2.413h-6.457a6.778 6.778 0 01.29-1.93h3.753c.04-.253.06-.483.06-.735 0-1.155-.593-1.805-1.738-1.805-1.616 0-2.361.95-2.888 2.326a5.94 5.94 0 00-.445 2.144h-.002v.091c0 .104.008.2.013.298h.002c.132 1.7 1.156 2.339 2.91 2.339 1.126 0 2.21-.294 3.233-.777l-.347 2.225c-1.064.336-2.23.608-3.356.608-3.029 0-5.075-1.51-5.075-4.721 0-3.357 2.538-6.589 5.996-6.589 2.598 0 4.317 1.363 4.317 4.113z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Ticketmaster"
});
